import { useContext, useState, useEffect } from "react";
import { NextSeo } from "next-seo";
import Image from "next/image";
import algoliasearch from "algoliasearch/lite";
import {
  InstantSearch,
  InstantSearchSSRProvider,
  Hits,
  useHits,
  Configure,
  Index,
} from "react-instantsearch-hooks-web";
import { getServerState } from "react-instantsearch-hooks-server";
import { renderToString } from "react-dom/server";

import styles from "./Brands.module.scss";
import productCardStyles from "@/components/product/ProductCard.module.scss";

import StoreContext from "@/context/store";
import { getJaneSpecials } from "@/lib/Jane";
import { useNextRouterHandler, decodeParam } from "@/hooks/useNextRouterHandler";

import ProductCard from "@/components/product/ProductCard";
import Pagination from "@/components/shop/Pagination";
import Stats from "@/components/shop/Stats";
import NoResults from "@/components/shop/NoResults";
import CurrentRefinements from "@/components/shop/CurrentRefinements";
import ShopNav from "@/components/shop/ShopNav";
import { getSiteSettings } from '@/lib/getSiteSettings';

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);
const productIndexName = "menu-products-by-rating-production";
const brandsIndexName = "product-brand-production";

export async function getServerSideProps({ req, params }) {
  const siteSettings = await getSiteSettings();
  const protocol = req.headers.referer?.split('://')[0] || 'https';
  const url = `${protocol}://${req.headers.host}${req.url}`;
  const brandName = params.brand.replace(/-/g, " ");
  const brandSlug = params.brand.replace(/ /g, "-");
  const initialStore = req.cookies.JANE_STORE || '4398';

  // Fetch brand hit
  const brandsIndex = searchClient.initIndex(brandsIndexName);
  const brandResult = await brandsIndex.search("", {
    filters: `name:\"${brandName}\"`,
    hitsPerPage: 1,
  });

  const brand = brandResult.hits?.[0] || null;

  const serverState = await getServerState(
    <BrandPage url={url} brandName={brandName} brandSlug={brandSlug} initialStore={initialStore} />,
    { renderToString }
  );

  return {
    props: {
      siteSettings,
      serverState,
      url,
      brandName,
      initialStore,
      brandSlug,
      brand,
    },
  };
};

export default function BrandPage({ serverState, url, brand, brandName, brandSlug, initialStore }) {

  const { initialUiState, NextRouterHandler } = useNextRouterHandler({
    dynamicRouteQuery: { brandName },
    url,
    // See https://www.algolia.com/doc/api-reference/widgets/ui-state/react-hooks/
    // for a more complete example structure of UI State.
    routeToState(params) {

      return {
        [productIndexName]: {
          query: params.q ? params.q : null,
          refinementList: {
            root_types: decodeParam(params.category, "root_types"),
            brand: brandSlug ? brandSlug : null,
            category: decodeParam(params.strain, "category"),
            feelings: decodeParam(params.effect, "feelings"),
            available_weights: decodeParam(params.weight, "available_weights"),
            applicable_special_ids: decodeParam(params.special, "applicable_special_ids"),
          },
          range: {
            bucket_price: params.price ? params.price : null,
            percent_thc: params.thc ? params.thc : null,
            percent_cbd: params.cbd ? params.cbd : null,
          },
          page: params.page ? params.page : null,
          sortBy: params.order ? params.order : null,
        },
      };
    },
    stateToRoute(uiState) {
      const indexUiState = uiState[productIndexName];

      const serializeParam = (param) =>
        param?.map((item) => item.toLowerCase().replace(/ /g, "+")).join(",");

      return {
        category: serializeParam(indexUiState.refinementList?.root_types),
        brand: brandSlug,
        strain: serializeParam(indexUiState.refinementList?.category),
        effect: serializeParam(indexUiState.refinementList?.feelings),
        weight: serializeParam(indexUiState.refinementList?.available_weights),
        special: serializeParam(
          indexUiState.refinementList?.applicable_special_ids
        ),
        price: indexUiState.range?.bucket_price,
        thc: indexUiState.range?.percent_thc,
        cbd: indexUiState.range?.percent_cbd,
        q: indexUiState.query,
        page: indexUiState.page,
        order: indexUiState.sortBy,
      };
    },
  });

  const { store } = useContext(StoreContext);
  const [list, setList] = useState(false);
  const [productSpecials, setProductSpecials] = useState([]);

  const seoTitle = brand?.name ? `${brand.name} | Purple Lotus` : "Brand Products | Purple Lotus";
  const seoDescription = brand?.description
    ? brand.description.replace(/<[^>]+>/g, "").trim().slice(0, 152) + "..."
    : `Shop ${brand?.name} cannabis products at Purple Lotus!`;

  // fetch jane product specials
  useEffect(() => {
    async function fetchSpecialsData() {
      if (!store) return;
      try {
        const specials = await getJaneSpecials(store, "product");
        setProductSpecials(specials);
      } catch (error) {
        console.error("Error fetching specials:", error);
      }
    }
    fetchSpecialsData();
  }, [store]);

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        openGraph={{
          title: seoTitle,
          description: seoDescription,
          images: brand?.logo_url
            ? [
              {
                url: brand.logo_url,
                width: 800,  // optional
                height: 600, // optional
                alt: `${brand.name} Logo`,
                type: 'image/png', // or 'image/svg+xml' if using SVG
              },
            ]
            : [],
        }}
      />
      <InstantSearchSSRProvider {...serverState}>
        <InstantSearch indexName={productIndexName} searchClient={searchClient} initialUiState={initialUiState}>

          <Index indexName={brandsIndexName}>
            <Configure filters={`name:"${brandName}"`} />
            <BrandHeader brandName={brandName} />
          </Index>

          <Index indexName={productIndexName}>
            <Configure filters={`store_id:${store ? store : initialStore} AND brand:"${brandName}"`} />

            <NextRouterHandler />
            <main className="container container__slim">
              <ShopNav list={list} setList={setList} url={url} productSpecials={productSpecials} />

              <div className={styles.stats}>
                <Index indexName={productIndexName}>
                  <Stats />
                </Index>
                <CurrentRefinements productSpecials={productSpecials} />
              </div>


              <div
                className={`${list ? productCardStyles.list : 'shop-product-grid'}`}
              >
                <NoResults>
                  <Hits hitComponent={ProductCard} />
                  <Pagination />
                </NoResults>
              </div>
            </main>
          </Index>
        </InstantSearch>
      </InstantSearchSSRProvider>
    </>
  );
}

function BrandHeader(props) {
  const { hits } = useHits(props);
  const brand = props.brandName;
  const hit = hits[0];

  function abbreviateLabel(label) {
    return label
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  }

  if (!hit) return "Loading...";

  // 🧼 Truncate and sanitize description for SEO
  const rawDescription = hit.description || `Shop ${brand} products at Purple Lotus`;
  const cleanText = rawDescription.replace(/<[^>]+>/g, "").trim();
  const safeDescription = cleanText.length > 155
    ? cleanText.slice(0, 152).trim() + "..."
    : cleanText;

  return (
    <div key={hit.name}>
      <div
        className={styles.headerContainer}
        style={{
          backgroundColor: hit.color_hex || "#663D98",
          height: hit.custom_image_url ? "300px" : "150px",
        }}
      >
        {hit.custom_image_url && (
          <div className={styles.image}>
            <Image
              src={hit.custom_image_url}
              alt="background-image"
              fill
              style={{ objectFit: "cover" }}
            />
          </div>
        )}
      </div>

      <div className={styles.brand}>
        <div className={styles.logo}>
          {hit.logo_url ? (
            <img
              src={hit.logo_url}
              alt={hit.brand}
              style={{ objectFit: "contain", width: "100%", height: "auto" }}
            />
          ) : (
            <div className={styles.abbr}>{abbreviateLabel(brand)}</div>
          )}
        </div>

        <div className={styles.brandInfo}>
          <h1>{hit.name}</h1>
          <p>{hit.description || `Purchase ${brand} cannabis products at Purple Lotus!`}</p>
        </div>
      </div>
    </div>
  );
}
